import React from 'react';
import {
  Box,
  Checkbox as MuiCheckbox,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  TextField,
  TextFieldProps
} from '@mui/material';

type Option = { label: string; value: string };

const baseInputSx = {
  '& .MuiInputBase-root': {
    background: 'rgba(0,0,0,0.70) !important',
    color: '#FDFDFD !important',
    borderRadius: '6px !important',
    outline: '1px solid #868686 !important',
    transition: 'outline-color .15s ease',
    '&:hover': { outlineColor: '#B4B4B4 !important' },
    '&.Mui-focused': { outlineColor: '#F79420 !important' }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important'
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px !important',
    fontSize: '14px !important',
    fontWeight: '400 !important',
    lineHeight: '20px !important',
    fontFamily: 'Inter, sans-serif !important',
    color: '#FDFDFD !important',
    background: 'rgba(0,0,0,0.70) !important'
  },
  '& input': {
    background: 'rgba(0,0,0,0.70) !important',
    fontFamily: 'Inter, sans-serif !important',
    fontSize: '14px !important',
    fontWeight: '400 !important',
    lineHeight: '20px !important',
    color: '#FDFDFD !important'
  },
  '& .MuiInputBase-input::placeholder': {
    color: '#868686 !important',
    opacity: 1
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 0
  }
};

export const UiInput: React.FC<TextFieldProps> = (props) => (
  <TextField
    {...props}
    variant="outlined"
    fullWidth={props.fullWidth ?? true}
    InputLabelProps={{ ...(props.InputLabelProps || {}), sx: { color: '#FDFDFD' } }}
    sx={{ ...baseInputSx, ...(props.sx as object) }}
  />
);

type UiSelectProps = {
  label?: string;
  placeholder?: string;
  value: any;
  onChange: (event: SelectChangeEvent<any>) => void;
  multiple?: boolean;
  options: Option[];
  renderValue?: (value: any) => React.ReactNode;
  errorText?: string;
};

export const UiSelect: React.FC<UiSelectProps> = ({
  label,
  placeholder,
  value,
  onChange,
  multiple,
  options,
  renderValue,
  errorText
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    {label && (
      <Box sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 500, lineHeight: '20px' }}>{label}</Box>
    )}
    <MuiSelect
      fullWidth
      multiple={multiple}
      value={value}
      onChange={onChange}
      displayEmpty
      renderValue={(selected) => {
        if ((Array.isArray(selected) && selected.length === 0) || (!Array.isArray(selected) && !selected)) {
          return <Box sx={{ color: '#868686' }}>{placeholder || 'Select'}</Box>;
        }
        if (renderValue) return renderValue(selected);
        if (Array.isArray(selected)) return selected.join(', ');
        return selected;
      }}
      sx={{
        ...baseInputSx,
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSelect-select': { color: '#FDFDFD' },
        '& .MuiSelect-icon': { color: '#FDFDFD' }
      }}
      MenuProps={{
        PaperProps: { sx: { bgcolor: '#000', color: '#FDFDFD' } }
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </MuiSelect>
    {errorText && (
      <Box sx={{ color: '#f44336', fontSize: 12, fontWeight: 400, lineHeight: '18px' }}>{errorText}</Box>
    )}
  </Box>
);

export const UiTextArea: React.FC<TextFieldProps> = (props) => (
  <UiInput {...props} multiline minRows={props.minRows || 4} />
);

type UiCheckboxProps = {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  label?: string;
};

export const UiCheckbox: React.FC<UiCheckboxProps> = ({ checked, onChange, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <MuiCheckbox
      checked={checked}
      onChange={onChange}
      icon={<Box sx={{ width: 16.5, height: 16.5, outline: '2px solid #868686', outlineOffset: '-1px' }} />}
      checkedIcon={
        <Box
          sx={{
            width: 16.5,
            height: 16.5,
            outline: '2px solid #FDFDFD',
            outlineOffset: '-1px',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 8,
              height: 4,
              borderLeft: '2px solid #FDFDFD',
              borderBottom: '2px solid #FDFDFD',
              transform: 'rotate(-45deg)',
              top: 4,
              left: 4
            }}
          />
        </Box>
      }
      sx={{
        p: 0,
        width: 22,
        height: 22
      }}
    />
    {label && <Box sx={{ color: '#FDFDFD', fontSize: 14 }}>{label}</Box>}
  </Box>
);

type UiSwitchProps = {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
};

export const UiSwitch: React.FC<UiSwitchProps> = ({ checked, onChange }) => (
  <Box
    component="span"
    sx={{
      width: 40,
      height: 20,
      borderRadius: 999,
      border: '1px solid #565656',
      background: '#1C1C1C',
      display: 'flex',
      alignItems: 'center',
      p: '2px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
    onClick={(e) => onChange(e as unknown as React.ChangeEvent<HTMLInputElement>, !checked)}
  >
    <Box
      sx={{
        width: 14,
        height: 14,
        borderRadius: 999,
        background: checked ? '#F79420' : '#565656',
        transform: checked ? 'translateX(18px)' : 'translateX(0px)',
        transition: 'transform 0.2s'
      }}
    />
  </Box>
);
